import type { Intersection, LaserIntersectionResult } from "@novorender/api";
import { quat, ReadonlyQuat, ReadonlyVec3, vec3 } from "gl-matrix";

import { PointSnapInfo } from "features/render/measureSnap";

import { LaserPoint } from "./outlineLaserSlice";

export const LASER_POS_Z_OFFSET = -0.001; // 1mm offset below the clicked position

export function getLaserGizmoCoords(pos: ReadonlyVec3, normal: ReadonlyVec3, lockedRotation: ReadonlyQuat | undefined) {
    const z0 = pos;
    const z1 = vec3.add(vec3.create(), pos, normal);

    const minI = Math.abs(normal[2]) < Math.cos(0.08726646) ? 2 : 1; //Try to align y axis to up or if normal is already pointing up, then north. 5 degrees test
    const axisY = vec3.fromValues(0, 0, 0);
    axisY[minI] = 1;
    const xDir = vec3.cross(vec3.create(), axisY, normal);
    if (lockedRotation) {
        vec3.transformQuat(xDir, xDir, lockedRotation);
    }
    vec3.normalize(xDir, xDir);
    const x0 = vec3.add(vec3.create(), pos, xDir);
    const x1 = vec3.scaleAndAdd(vec3.create(), pos, xDir, -1);
    const yDir = vec3.cross(vec3.create(), normal, xDir);
    vec3.normalize(yDir, yDir);
    const y0 = vec3.add(vec3.create(), pos, yDir);
    const y1 = vec3.scaleAndAdd(vec3.create(), pos, yDir, -1);

    return { x0, x1, xDir, y0, y1, yDir, z0, z1 };
}

export function getLaserGizmoAlignmentRotation(
    intersection: Intersection,
    pos: ReadonlyVec3,
    xDir: ReadonlyVec3,
    yDir: ReadonlyVec3,
): quat | undefined {
    const l0 = intersection.left?.[0];
    const r0 = intersection.right?.[0];
    if (l0 && r0) {
        const alignedDir = vec3.sub(vec3.create(), r0, pos);
        vec3.normalize(alignedDir, alignedDir);
        return quat.rotationTo(quat.create(), xDir, alignedDir);
    }

    const u0 = intersection.up?.[0];
    const d0 = intersection.down?.[0];
    if (u0 && d0) {
        const alignedDir = vec3.sub(vec3.create(), d0, pos);
        vec3.normalize(alignedDir, alignedDir);
        return quat.rotationTo(quat.create(), yDir, alignedDir);
    }
}

export function intersectionToLaserPoint(point: LaserIntersectionResult["left"][0]): LaserPoint {
    return {
        position: point.position,
        objectId: point.objectId,
        normal: point.normal,
        connection: (point.tangent
            ? {
                  connection: point.position,
                  type: "tangent",
                  start: vec3.add(vec3.create(), point.position, point.tangent) as ReadonlyVec3,
                  end: vec3.scaleAndAdd(vec3.create(), point.position, point.tangent, -1) as ReadonlyVec3,
              }
            : {
                  connection: point.position,
                  type: "normal",
                  normal: point.normal,
              }) as PointSnapInfo,
    };
}
